import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ApiService } from '../../../core/api-service';
import { FeedbackService } from '../../../core/feedback.service';
import { EMPTY, mergeMap, Observable, of, switchMap } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { selectAccountID } from '../../../store/global/core-selectors';
import { beError, UserActions, UsersActions, usersComponentDestroyed } from './users-actions';
import { EditUserPayload } from '../../../models/users-models/edit-user.payload';
import { UserPayload } from '../../../models/users-models/user.payload';
import { ApiResponse, ApiResponseStatus } from '../../../models/api-response';
import { TranslateService } from '@ngx-translate/core';
import { blsUser } from '../../../models/users-models/blsUser';

@Injectable()
export class UsersEffects {
  constructor(private actions$: Actions,
              private store: Store,
              private apiService: ApiService,
              private translate: TranslateService,
              private feedbackService: FeedbackService) {
  }

  updateBlsUsers$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(UsersActions.getUsers, usersComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        switchMap(([action, accountId]) => action.type ===
            usersComponentDestroyed.type ? EMPTY : this.apiService.getBlsUsers(accountId)
                .pipe(
                    mergeMap((res) => [
                      UsersActions.getUsersSuccess({ usersResult: res?.result }),
                      UsersActions.updateBlsUsersLoading({ blsUsersLoading: false }),
                    ]),
                    catchError((err: HttpErrorResponse) => of(beError({
                      userFriendlyMessage: 'Getting BLS users failed.',
                      error: err,
                    })).pipe(
                        mergeMap((action1) => [action1, UsersActions.updateBlsUsersLoading({ blsUsersLoading: false })]),
                    )),
                ),
        ),
    );
  });

  setBlsUsersLoading$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(UsersActions.getUsers),
        map(() => UsersActions.updateBlsUsersLoading({ blsUsersLoading: true })),
    );
  });

  apiCallError$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(beError),
        tap((action) => {
          this.feedbackService.showAlertSnackbar({
            data: {
              userFriendlyMessage: action.userFriendlyMessage,
              detailedInfoFromBE: action.error?.error?.message ? [action.error.error.message] : null,
            }, isAddressListSnackbar: false,
          }, action.error?.status);
        }),
    );
  }, { dispatch: false });

  createUser$ = createEffect(() => {
    return this.actions$.pipe(
        ofType(UserActions.createUser, usersComponentDestroyed),
        concatLatestFrom(() => this.store.select(selectAccountID)),
        mergeMap(([action, accountId]) => {
          let res$: Observable<ApiResponse<blsUser>>;
          // early return
          if (action.type === usersComponentDestroyed.type) {
            return EMPTY;
          }

          if (action.editMode) {
            res$ = this.apiService.editUser(accountId, action.userParams as EditUserPayload);
          } else {
            res$ = this.apiService.createUser(accountId, action.userParams as UserPayload);
          }

          return res$.pipe(
              // show success / warning snackbar (side effect)
              tap((userResponse) => {
                if (userResponse.status === ApiResponseStatus.OK) {
                  this.feedbackService.showSuccessSnackbar({
                    data: {
                      userFriendlyMessage: this.translate.instant(action.editMode ?
                          'USERS.EDIT_SUCCESS' :
                          'USERS.ACCESS_EMAIL_SENT'),
                    },
                    isAddressListSnackbar: false,
                  });
                  // User created, but there was an issue with sending email
                } else if (userResponse.status === ApiResponseStatus.WARNING) {
                  this.feedbackService.showWarningSnackbar({
                    data: {
                      userFriendlyMessage: userResponse.message,
                    },
                    isAddressListSnackbar: false,
                  });
                }
              }),
              // return store update action
              map((userResponse) => {
                return UserActions.userCreated({ user: userResponse?.result });
              }),
              // return error action
              catchError((err: HttpErrorResponse) => of(beError({
                userFriendlyMessage: this.translate.instant('ERROR'),
                error: err,
              }))),
          );
        }),
    );
  });


}
